import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { apiClient } from '../../utils/simpleApiClient';

interface PricePoint {
  id?: number;
  kartra_price_point: string;
  billing_cycle: string;
  billing_interval: number;
  billing_interval_unit: string;
  price: string;
  currency: string;
  base_monthly_equivalent: string;
  savings_percentage: string;
  display_name: string;
  is_default: boolean;
  is_active: boolean;
}

interface Feature {
  id?: number;
  feature_name: string;
  feature_description?: string;
  icon?: string;
  sort_order: number;
  is_highlighted: boolean;
}

interface Limit {
  id?: number;
  limit_type: string;
  limit_value: string;
  display_label: string;
  display_value: string;
  sort_order: number;
}

interface Product {
  id?: number;
  kartra_product_id: string;
  kartra_product_name: string;
  product_slug: string;
  display_name: string;
  tagline: string;
  description: string;
  sort_order: number;
  is_active: boolean;
  is_featured: boolean;
  is_popular: boolean;
  price_points?: PricePoint[];
  features?: Feature[];
  limits?: Limit[];
}

interface ProductFormProps {
  product: Product | null;
  onClose: (shouldRefresh: boolean) => void;
}

const ProductForm: React.FC<ProductFormProps> = ({ product, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Product>({
    kartra_product_id: '',
    kartra_product_name: '',
    product_slug: '',
    display_name: '',
    tagline: '',
    description: '',
    sort_order: 1,
    is_active: true,
    is_featured: false,
    is_popular: false,
    price_points: [],
    features: [],
    limits: []
  });

  useEffect(() => {
    if (product) {
      setFormData(product);
    }
  }, [product]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = product
        ? `/api/admin/products/${product.id}`
        : '/api/admin/products';

      // Transform snake_case to camelCase for API
      const payload = {
        ...formData,
        pricePoints: formData.price_points || [],
        features: formData.features || [],
        limits: formData.limits || [],
      };

      // Remove snake_case versions
      delete (payload as any).price_points;

      // Use apiClient which handles CSRF tokens and authentication
      const response = product
        ? await apiClient.put(url, payload)
        : await apiClient.post(url, payload);

      if (response && response.success) {
        toast.success(product ? 'Product updated successfully' : 'Product created successfully');
        onClose(true);
      } else {
        toast.error(response?.message || 'Failed to save product');
      }
    } catch (error: any) {
      console.error('Error saving product:', error);
      toast.error(error.message || 'Error saving product');
    } finally {
      setLoading(false);
    }
  };

  const addPricePoint = () => {
    setFormData({
      ...formData,
      price_points: [
        ...(formData.price_points || []),
        {
          kartra_price_point: '',
          billing_cycle: 'monthly',
          billing_interval: 1,
          billing_interval_unit: 'month',
          price: '0.00',
          currency: 'USD',
          base_monthly_equivalent: '0.00',
          savings_percentage: '0.00',
          display_name: 'Monthly',
          is_default: false,
          is_active: true
        }
      ]
    });
  };

  const removePricePoint = (index: number) => {
    const newPricePoints = [...(formData.price_points || [])];
    newPricePoints.splice(index, 1);
    setFormData({ ...formData, price_points: newPricePoints });
  };

  const updatePricePoint = (index: number, field: keyof PricePoint, value: any) => {
    const newPricePoints = [...(formData.price_points || [])];
    newPricePoints[index] = { ...newPricePoints[index], [field]: value };
    setFormData({ ...formData, price_points: newPricePoints });
  };

  const addFeature = () => {
    setFormData({
      ...formData,
      features: [
        ...(formData.features || []),
        {
          feature_name: '',
          feature_description: '',
          sort_order: (formData.features?.length || 0) + 1,
          is_highlighted: false
        }
      ]
    });
  };

  const removeFeature = (index: number) => {
    const newFeatures = [...(formData.features || [])];
    newFeatures.splice(index, 1);
    setFormData({ ...formData, features: newFeatures });
  };

  const updateFeature = (index: number, field: keyof Feature, value: any) => {
    const newFeatures = [...(formData.features || [])];
    newFeatures[index] = { ...newFeatures[index], [field]: value };
    setFormData({ ...formData, features: newFeatures });
  };

  const addLimit = () => {
    setFormData({
      ...formData,
      limits: [
        ...(formData.limits || []),
        {
          limit_type: '',
          limit_value: '',
          display_label: '',
          display_value: '',
          sort_order: (formData.limits?.length || 0) + 1
        }
      ]
    });
  };

  const removeLimit = (index: number) => {
    const newLimits = [...(formData.limits || [])];
    newLimits.splice(index, 1);
    setFormData({ ...formData, limits: newLimits });
  };

  const updateLimit = (index: number, field: keyof Limit, value: any) => {
    const newLimits = [...(formData.limits || [])];
    newLimits[index] = { ...newLimits[index], [field]: value };
    setFormData({ ...formData, limits: newLimits });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">
            {product ? 'Edit Product' : 'Create New Product'}
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            {product ? 'Update product details, pricing, and features' : 'Add a new product to the system'}
          </p>
        </div>
        <button
          onClick={() => onClose(false)}
          className="text-slate-400 hover:text-white transition-colors duration-150"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl shadow-xl border border-slate-700/50 p-6">
          <h3 className="text-lg font-medium text-white mb-4">Basic Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Display Name *
              </label>
              <input
                type="text"
                required
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/20 transition-all duration-200"
                placeholder="Standard Plan"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Product Slug *
              </label>
              <input
                type="text"
                required
                value={formData.product_slug}
                onChange={(e) => setFormData({ ...formData, product_slug: e.target.value })}
                className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/20 transition-all duration-200"
                placeholder="standard"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Kartra Product ID *
              </label>
              <input
                type="text"
                required
                value={formData.kartra_product_id}
                onChange={(e) => setFormData({ ...formData, kartra_product_id: e.target.value })}
                className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/20 transition-all duration-200"
                placeholder="525141"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Kartra Product Name *
              </label>
              <input
                type="text"
                required
                value={formData.kartra_product_name}
                onChange={(e) => setFormData({ ...formData, kartra_product_name: e.target.value })}
                className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/20 transition-all duration-200"
                placeholder="Standard Plan"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Tagline
              </label>
              <input
                type="text"
                value={formData.tagline}
                onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/20 transition-all duration-200"
                placeholder="Perfect for small businesses"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/20 transition-all duration-200"
                placeholder="AI-powered website analysis with basic features"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Sort Order
              </label>
              <input
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
                className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/20 transition-all duration-200"
              />
            </div>
            <div className="flex items-center space-x-6 pt-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-indigo-600 bg-slate-700 border-slate-600 rounded focus:ring-indigo-500 focus:ring-offset-slate-800"
                />
                <span className="ml-2 text-sm text-slate-300">Active</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_popular}
                  onChange={(e) => setFormData({ ...formData, is_popular: e.target.checked })}
                  className="w-4 h-4 text-indigo-600 bg-slate-700 border-slate-600 rounded focus:ring-indigo-500 focus:ring-offset-slate-800"
                />
                <span className="ml-2 text-sm text-slate-300">Popular</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  className="w-4 h-4 text-indigo-600 bg-slate-700 border-slate-600 rounded focus:ring-indigo-500 focus:ring-offset-slate-800"
                />
                <span className="ml-2 text-sm text-slate-300">Featured</span>
              </label>
            </div>
          </div>
        </div>

        {/* Price Points */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl shadow-xl border border-slate-700/50 p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-white">Price Points</h3>
            <button
              type="button"
              onClick={addPricePoint}
              className="inline-flex items-center px-3 py-1.5 text-sm text-indigo-400 hover:text-indigo-300 transition-colors duration-150"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Price Point
            </button>
          </div>
          <div className="space-y-4">
            {formData.price_points?.map((pricePoint, index) => (
              <div key={index} className="relative border border-slate-600/50 rounded-xl p-4 bg-slate-700/30">
                <button
                  type="button"
                  onClick={() => removePricePoint(index)}
                  className="absolute top-2 right-2 text-red-400 hover:text-red-300"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Billing Cycle</label>
                    <select
                      value={pricePoint.billing_cycle}
                      onChange={(e) => updatePricePoint(index, 'billing_cycle', e.target.value)}
                      className="w-full px-3 py-2 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white text-sm"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="annual">Annual</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Price (USD)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={pricePoint.price}
                      onChange={(e) => updatePricePoint(index, 'price', e.target.value)}
                      className="w-full px-3 py-2 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Kartra Price Point</label>
                    <input
                      type="text"
                      value={pricePoint.kartra_price_point}
                      onChange={(e) => updatePricePoint(index, 'kartra_price_point', e.target.value)}
                      className="w-full px-3 py-2 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white text-sm"
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-4 mt-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={pricePoint.is_default}
                      onChange={(e) => updatePricePoint(index, 'is_default', e.target.checked)}
                      className="w-3 h-3 text-indigo-600 bg-slate-700 border-slate-600 rounded focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-xs text-slate-300">Default</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={pricePoint.is_active}
                      onChange={(e) => updatePricePoint(index, 'is_active', e.target.checked)}
                      className="w-3 h-3 text-indigo-600 bg-slate-700 border-slate-600 rounded focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-xs text-slate-300">Active</span>
                  </label>
                </div>
              </div>
            ))}
            {!formData.price_points?.length && (
              <p className="text-sm text-slate-400 text-center py-4">No price points added yet</p>
            )}
          </div>
        </div>

        {/* Features */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl shadow-xl border border-slate-700/50 p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-white">Features</h3>
            <button
              type="button"
              onClick={addFeature}
              className="inline-flex items-center px-3 py-1.5 text-sm text-indigo-400 hover:text-indigo-300 transition-colors duration-150"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Feature
            </button>
          </div>
          <div className="space-y-3">
            {formData.features?.map((feature, index) => (
              <div key={index} className="relative border border-slate-600/50 rounded-xl p-3 bg-slate-700/30">
                <button
                  type="button"
                  onClick={() => removeFeature(index)}
                  className="absolute top-2 right-2 text-red-400 hover:text-red-300"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
                <div className="grid grid-cols-1 gap-3 pr-8">
                  <input
                    type="text"
                    value={feature.feature_name}
                    onChange={(e) => updateFeature(index, 'feature_name', e.target.value)}
                    placeholder="Feature name"
                    className="w-full px-3 py-2 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white text-sm"
                  />
                </div>
              </div>
            ))}
            {!formData.features?.length && (
              <p className="text-sm text-slate-400 text-center py-4">No features added yet</p>
            )}
          </div>
        </div>

        {/* Limits */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl shadow-xl border border-slate-700/50 p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-white">Limits</h3>
            <button
              type="button"
              onClick={addLimit}
              className="inline-flex items-center px-3 py-1.5 text-sm text-indigo-400 hover:text-indigo-300 transition-colors duration-150"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Limit
            </button>
          </div>
          <div className="space-y-3">
            {formData.limits?.map((limit, index) => (
              <div key={index} className="relative border border-slate-600/50 rounded-xl p-3 bg-slate-700/30">
                <button
                  type="button"
                  onClick={() => removeLimit(index)}
                  className="absolute top-2 right-2 text-red-400 hover:text-red-300"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pr-8">
                  <input
                    type="text"
                    value={limit.limit_type}
                    onChange={(e) => updateLimit(index, 'limit_type', e.target.value)}
                    placeholder="Limit type (e.g., websites)"
                    className="w-full px-3 py-2 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white text-sm"
                  />
                  <input
                    type="text"
                    value={limit.limit_value}
                    onChange={(e) => updateLimit(index, 'limit_value', e.target.value)}
                    placeholder="Value (e.g., 2 or unlimited)"
                    className="w-full px-3 py-2 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white text-sm"
                  />
                  <input
                    type="text"
                    value={limit.display_label}
                    onChange={(e) => updateLimit(index, 'display_label', e.target.value)}
                    placeholder="Display label"
                    className="w-full px-3 py-2 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white text-sm"
                  />
                  <input
                    type="text"
                    value={limit.display_value}
                    onChange={(e) => updateLimit(index, 'display_value', e.target.value)}
                    placeholder="Display value"
                    className="w-full px-3 py-2 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white text-sm"
                  />
                </div>
              </div>
            ))}
            {!formData.limits?.length && (
              <p className="text-sm text-slate-400 text-center py-4">No limits added yet</p>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => onClose(false)}
            className="px-6 py-2 border border-slate-600/50 rounded-xl shadow-sm text-sm font-medium text-slate-300 bg-slate-700/50 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-br from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            {loading ? 'Saving...' : (product ? 'Update Product' : 'Create Product')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;
